import React from 'react';
import {
  Content,
  ContentHeader,
  Header,
  HeaderLabel,
  Page,
  SupportButton,
} from '@backstage/core-components';
import { useApi, fetchApiRef, configApiRef } from '@backstage/core-plugin-api';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import EditIcon from '@material-ui/icons/Edit';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import NotificationsIcon from '@material-ui/icons/Notifications';
import PauseCircleFilledIcon from '@material-ui/icons/PauseCircleFilled';

/* ─── Types ─── */

interface GrafanaAlertRule {
  uid: string;
  title: string;
  condition: string;
  data: GrafanaQueryData[];
  ruleGroup: string;
  folderUID: string;
  for: string;
  labels?: Record<string, string>;
  annotations?: Record<string, string>;
  isPaused?: boolean;
  provenance?: string; // 'file'=파일 프로비저닝(=UI 수정 불가, Git이 SoT)
  // extracted for display
  _threshold?: number;
  _operator?: string;
  _expr?: string;
}

interface GrafanaQueryData {
  refId: string;
  queryType: string;
  relativeTimeRange?: { from: number; to: number };
  datasourceUid?: string;
  model?: Record<string, unknown>;
}

interface RuleGroup {
  name: string;
  rules: GrafanaAlertRule[];
  expanded: boolean;
}

/* ─── Alert Center Types ─── */

interface GrafanaAlertInstance {
  labels: Record<string, string>;
  annotations?: Record<string, string>;
  state: 'Normal' | 'Pending' | 'Alerting' | 'NoData' | 'Error';
  activeAt?: string;
  value?: string;
}

interface GrafanaRuleStatus {
  name: string;
  state: string;
  health: string;
  lastEvaluation?: string;
  evaluationTime?: number;
  alerts?: GrafanaAlertInstance[];
  labels?: Record<string, string>;
  annotations?: Record<string, string>;
  type: string;
}

interface GrafanaGroupStatus {
  name: string;
  file: string;
  rules: GrafanaRuleStatus[];
  interval: number;
}

interface FlatAlert {
  ruleName: string;
  group: string;
  severity: string;
  component: string;
  state: string;
  summary: string;
  activeAt: string;
  value: string;
  lastEvaluation: string;
  appName: string;
}

/* ─── Styles ─── */

const useStyles = makeStyles(theme => ({
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    cursor: 'pointer',
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  groupTitle: {
    fontWeight: 600,
    fontSize: '0.9rem',
  },
  severityChip: {
    height: 22,
    fontWeight: 600,
    fontSize: '0.7rem',
  },
  ruleTitle: {
    fontWeight: 500,
  },
  thresholdCell: {
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  forCell: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
  },
  statusOk: { color: '#4caf50' },
  statusWarn: { color: '#ff9800' },
  statusCrit: { color: '#f44336' },
  statusPaused: { color: '#9e9e9e' },
  statsRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  statCard: {
    flex: 1,
    textAlign: 'center' as const,
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
  },
  statLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
  },
  tabBar: {
    marginBottom: theme.spacing(2),
  },
  stateChip: {
    height: 22,
    fontWeight: 600,
    fontSize: '0.7rem',
  },
  mono: {
    fontFamily: 'monospace',
    fontSize: '0.78rem',
  },
  timelineRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  filterRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap' as const,
  },
  formControl: {
    minWidth: 140,
  },
}));

/* ─── Helpers ─── */

const GRAFANA_PROXY = '/api/proxy/grafana-admin';

function extractThreshold(rule: GrafanaAlertRule): { threshold: number | null; operator: string; expr: string } {
  // Find the threshold condition in data array (refId C = threshold)
  const thresholdData = rule.data?.find(d => d.refId === 'C' || d.queryType === 'classic_conditions');
  if (thresholdData?.model) {
    const conditions = (thresholdData.model as Record<string, unknown>).conditions as Array<Record<string, unknown>> | undefined;
    if (conditions?.[0]) {
      const evaluator = conditions[0].evaluator as Record<string, unknown> | undefined;
      if (evaluator) {
        const params = evaluator.params as number[] | undefined;
        const type = evaluator.type as string | undefined;
        return {
          threshold: params?.[0] ?? null,
          operator: type ?? 'gt',
          expr: '',
        };
      }
    }
  }

  // Try reduce+threshold pattern (Grafana 10+ provisioning)
  const threshData = rule.data?.find(d => d.refId === 'C');
  if (threshData?.model) {
    const conditions = (threshData.model as Record<string, unknown>).conditions as Array<Record<string, unknown>> | undefined;
    if (conditions?.[0]) {
      const evaluator = conditions[0].evaluator as Record<string, unknown> | undefined;
      if (evaluator) {
        const params = evaluator.params as number[] | undefined;
        return {
          threshold: params?.[0] ?? null,
          operator: (evaluator.type as string) ?? 'gt',
          expr: '',
        };
      }
    }
  }

  // Extract PromQL from data[0]
  const promData = rule.data?.find(d => d.refId === 'A');
  const expr = (promData?.model as Record<string, unknown>)?.expr as string ?? '';

  return { threshold: null, operator: '', expr };
}

function severityColor(severity?: string): string {
  switch (severity) {
    case 'critical': return '#f44336';
    case 'warning': return '#ff9800';
    case 'info': return '#2196f3';
    default: return '#9e9e9e';
  }
}

function formatFor(forStr: string): string {
  if (!forStr || forStr === '0s') return '-';
  return forStr;
}

function operatorLabel(op: string): string {
  switch (op) {
    case 'gt': return '>';
    case 'lt': return '<';
    case 'gte': return '>=';
    case 'lte': return '<=';
    case 'eq': return '=';
    default: return op;
  }
}

/* ─── Alert Center Helpers ─── */

function acStateColor(state: string): string {
  switch (state) {
    case 'Alerting': case 'firing': return '#f44336';
    case 'Pending': return '#ff9800';
    case 'Normal': case 'inactive': return '#4caf50';
    case 'NoData': return '#9e9e9e';
    case 'Error': return '#d32f2f';
    default: return '#bdbdbd';
  }
}

function acStateIcon(state: string) {
  const size = 18;
  switch (state) {
    case 'Alerting': case 'firing':
      return <ErrorIcon style={{ fontSize: size, color: '#f44336' }} />;
    case 'Pending':
      return <WarningIcon style={{ fontSize: size, color: '#ff9800' }} />;
    case 'Normal': case 'inactive':
      return <CheckCircleIcon style={{ fontSize: size, color: '#4caf50' }} />;
    default:
      return <FiberManualRecordIcon style={{ fontSize: size, color: '#bdbdbd' }} />;
  }
}

function acStateLabel(state: string): string {
  switch (state) {
    case 'Alerting': case 'firing': return '발생 중';
    case 'Pending': return '대기';
    case 'Normal': case 'inactive': return '정상';
    case 'NoData': return '데이터 없음';
    case 'Error': return '오류';
    default: return state;
  }
}

function acFormatTime(isoStr?: string): string {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  const now = new Date();
  const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000);
  if (diffMin < 1) return '방금';
  if (diffMin < 60) return `${diffMin}분 전`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}시간 전`;
  return d.toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/* ─── Env Filter ─── */

function isDevApp(name: string): boolean {
  return name.startsWith('dev-') || name.endsWith('-dev');
}

function matchesEnv(alert: FlatAlert, isPrd: boolean): boolean {
  // Alerts without app name (system metrics) show in both
  if (!alert.appName) return true;
  if (isPrd) return !isDevApp(alert.appName);
  return isDevApp(alert.appName);
}

/* ─── Component ─── */

export const AlertRulesPage = () => {
  const classes = useStyles();
  const fetchApi = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const isPrd = !config.getString('app.baseUrl').includes('-dev');

  // Tab state
  const [activeTab, setActiveTab] = React.useState(0);

  // Rules tab state
  const [groups, setGroups] = React.useState<RuleGroup[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Edit dialog state
  const [editRule, setEditRule] = React.useState<GrafanaAlertRule | null>(null);
  const [editThreshold, setEditThreshold] = React.useState('');
  const [editFor, setEditFor] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  // Alert Center state
  const [alerts, setAlerts] = React.useState<FlatAlert[]>([]);
  const [acLoading, setAcLoading] = React.useState(true);
  const [acError, setAcError] = React.useState<string | null>(null);
  const [stateFilter, setStateFilter] = React.useState<string>('all');
  const [severityFilter, setSeverityFilter] = React.useState<string>('all');

  const fetchRules = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchApi.fetch(`${GRAFANA_PROXY}/api/v1/provisioning/alert-rules`);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Grafana API: ${res.status} ${text}`);
      }
      const rules: GrafanaAlertRule[] = await res.json();

      // Enrich with extracted threshold
      rules.forEach(r => {
        const { threshold, operator } = extractThreshold(r);
        r._threshold = threshold ?? undefined;
        r._operator = operator;
      });

      // Group by ruleGroup
      const groupMap = new Map<string, GrafanaAlertRule[]>();
      rules.forEach(r => {
        const g = r.ruleGroup || 'default';
        if (!groupMap.has(g)) groupMap.set(g, []);
        groupMap.get(g)!.push(r);
      });

      setGroups(
        Array.from(groupMap.entries()).map(([name, rls]) => ({
          name,
          rules: rls,
          expanded: true,
        })),
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [fetchApi]);

  React.useEffect(() => { fetchRules(); }, [fetchRules]);

  // Alert Center fetch
  const fetchAlerts = React.useCallback(async () => {
    setAcLoading(true);
    setAcError(null);
    try {
      const res = await fetchApi.fetch(`${GRAFANA_PROXY}/api/prometheus/grafana/api/v1/rules`);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Grafana API: ${res.status} ${text}`);
      }
      const json = await res.json();
      const acGroups: GrafanaGroupStatus[] = json.data?.groups ?? [];

      const flat: FlatAlert[] = [];
      acGroups.forEach(g => {
        g.rules.forEach(rule => {
          if (rule.alerts && rule.alerts.length > 0) {
            rule.alerts.forEach(alert => {
              flat.push({
                ruleName: rule.name,
                group: g.name,
                severity: alert.labels?.severity ?? rule.labels?.severity ?? '-',
                component: alert.labels?.component ?? rule.labels?.component ?? '-',
                state: alert.state,
                summary: alert.annotations?.summary ?? rule.annotations?.summary ?? '',
                activeAt: alert.activeAt ?? '',
                value: alert.value ?? '',
                lastEvaluation: rule.lastEvaluation ?? '',
                appName: alert.labels?.name ?? '',
              });
            });
          } else {
            flat.push({
              ruleName: rule.name,
              group: g.name,
              severity: rule.labels?.severity ?? '-',
              component: rule.labels?.component ?? '-',
              state: rule.state,
              summary: rule.annotations?.summary ?? '',
              activeAt: '',
              value: '',
              lastEvaluation: rule.lastEvaluation ?? '',
              appName: '',
            });
          }
        });
      });

      const statePrio: Record<string, number> = { Alerting: 0, firing: 0, Pending: 1, Error: 1, NoData: 2, Normal: 3, inactive: 3 };
      flat.sort((a, b) => (statePrio[a.state] ?? 9) - (statePrio[b.state] ?? 9));
      setAlerts(flat);
    } catch (e: unknown) {
      setAcError(e instanceof Error ? e.message : String(e));
    } finally {
      setAcLoading(false);
    }
  }, [fetchApi]);

  React.useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  // Auto-refresh alerts 30s
  React.useEffect(() => {
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const toggleGroup = (name: string) => {
    setGroups(prev =>
      prev.map(g => (g.name === name ? { ...g, expanded: !g.expanded } : g)),
    );
  };

  const openEdit = (rule: GrafanaAlertRule) => {
    setEditRule(rule);
    setEditThreshold(rule._threshold?.toString() ?? '');
    setEditFor(rule.for ?? '5m');
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!editRule) return;
    setSaving(true);
    setSaveError(null);

    try {
      // Fetch current rule to get full body
      const res = await fetchApi.fetch(
        `${GRAFANA_PROXY}/api/v1/provisioning/alert-rules/${editRule.uid}`,
      );
      if (!res.ok) throw new Error(`Fetch rule: ${res.status}`);
      const fullRule = await res.json();

      // Update threshold in data[C] conditions
      const newThreshold = parseFloat(editThreshold);
      if (isNaN(newThreshold)) throw new Error('유효한 숫자를 입력해주세요.');

      const threshData = fullRule.data?.find((d: GrafanaQueryData) => d.refId === 'C');
      if (threshData?.model?.conditions?.[0]?.evaluator?.params) {
        threshData.model.conditions[0].evaluator.params = [newThreshold];
      }

      // Update for duration
      fullRule.for = editFor;

      // PUT update
      const putRes = await fetchApi.fetch(
        `${GRAFANA_PROXY}/api/v1/provisioning/alert-rules/${editRule.uid}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'X-Disable-Provenance': 'true' },
          body: JSON.stringify(fullRule),
        },
      );
      if (!putRes.ok) {
        const text = await putRes.text();
        throw new Error(`Update failed: ${putRes.status} ${text}`);
      }

      setEditRule(null);
      fetchRules();
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  // Rules Stats
  const totalRules = groups.reduce((acc, g) => acc + g.rules.length, 0);
  const criticalCount = groups.reduce(
    (acc, g) => acc + g.rules.filter(r => r.labels?.severity === 'critical').length,
    0,
  );
  const warningCount = groups.reduce(
    (acc, g) => acc + g.rules.filter(r => r.labels?.severity === 'warning').length,
    0,
  );
  const pausedCount = groups.reduce(
    (acc, g) => acc + g.rules.filter(r => r.isPaused).length,
    0,
  );

  // Alert Center Stats & Filter (env-aware)
  const envAlerts = alerts.filter(a => matchesEnv(a, isPrd));
  const filteredAlerts = envAlerts.filter(a => {
    if (stateFilter !== 'all' && a.state !== stateFilter) return false;
    if (severityFilter !== 'all' && a.severity !== severityFilter) return false;
    return true;
  });
  const alertingCount = envAlerts.filter(a => a.state === 'Alerting' || a.state === 'firing').length;
  const pendingCount = envAlerts.filter(a => a.state === 'Pending').length;
  const normalCount = envAlerts.filter(a => a.state === 'Normal' || a.state === 'inactive').length;

  return (
    <Page themeId="tool">
      <Header title="Alert Rules" subtitle="Grafana Unified Alerting 관리">
        <HeaderLabel label="환경" value={isPrd ? 'Production' : 'Development'} />
        <HeaderLabel label="Engine" value="Grafana" />
        <HeaderLabel label="Rules" value={totalRules.toString()} />
      </Header>
      <Content>
        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
          className={classes.tabBar}
        >
          <Tab label={`룰 관리 (${totalRules})`} />
          <Tab label={`실시간 현황 (${alertingCount > 0 ? `${alertingCount} 발생` : '정상'})`} />
        </Tabs>

        {/* ═══ Tab 0: 룰 관리 ═══ */}
        {activeTab === 0 && (
          <>
            <ContentHeader title="">
              <Tooltip title="새로고침">
                <IconButton onClick={fetchRules} disabled={loading}>
                  {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
              <SupportButton>
                Grafana Unified Alerting 기반 알람 룰입니다. 룰은
                Git(grafana-config.yaml)으로 관리되며(파일 프로비저닝),
                임계치 변경은 코드 수정 + 배포로 반영됩니다.
              </SupportButton>
            </ContentHeader>

            {/* Stats */}
            <Box className={classes.statsRow}>
              <Card variant="outlined" className={classes.statCard}>
                <CardContent>
                  <Typography className={classes.statValue}>{totalRules}</Typography>
                  <Typography className={classes.statLabel}>전체 룰</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" className={classes.statCard}>
                <CardContent>
                  <Typography className={classes.statValue} style={{ color: '#f44336' }}>{criticalCount}</Typography>
                  <Typography className={classes.statLabel}>Critical</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" className={classes.statCard}>
                <CardContent>
                  <Typography className={classes.statValue} style={{ color: '#ff9800' }}>{warningCount}</Typography>
                  <Typography className={classes.statLabel}>Warning</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" className={classes.statCard}>
                <CardContent>
                  <Typography className={classes.statValue} style={{ color: '#9e9e9e' }}>{pausedCount}</Typography>
                  <Typography className={classes.statLabel}>Paused</Typography>
                </CardContent>
              </Card>
            </Box>

            {error && (
              <Box mb={2}>
                <Typography color="error">{error}</Typography>
              </Box>
            )}

            {/* Rule Groups */}
            {groups.map(group => (
          <Card key={group.name} variant="outlined" style={{ marginBottom: 16 }}>
            <Box
              className={classes.groupHeader}
              onClick={() => toggleGroup(group.name)}
            >
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                {group.expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                <Typography className={classes.groupTitle}>{group.name}</Typography>
                <Chip
                  label={`${group.rules.length} rules`}
                  size="small"
                  variant="outlined"
                  style={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            </Box>
            <Collapse in={group.expanded}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>상태</TableCell>
                      <TableCell>룰 이름</TableCell>
                      <TableCell>심각도</TableCell>
                      <TableCell align="right">임계치</TableCell>
                      <TableCell>대기</TableCell>
                      <TableCell>컴포넌트</TableCell>
                      <TableCell align="center">편집</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.rules.map(rule => {
                      const severity = rule.labels?.severity ?? 'unknown';
                      const component = rule.labels?.component ?? '-';
                      return (
                        <TableRow key={rule.uid} hover>
                          <TableCell>
                            {rule.isPaused ? (
                              <PauseCircleFilledIcon className={classes.statusPaused} style={{ fontSize: 20 }} />
                            ) : severity === 'critical' ? (
                              <ErrorIcon className={classes.statusCrit} style={{ fontSize: 20 }} />
                            ) : severity === 'warning' ? (
                              <WarningIcon className={classes.statusWarn} style={{ fontSize: 20 }} />
                            ) : (
                              <CheckCircleIcon className={classes.statusOk} style={{ fontSize: 20 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography className={classes.ruleTitle} variant="body2">
                              {rule.title}
                            </Typography>
                            {rule.annotations?.summary && (
                              <Typography variant="caption" color="textSecondary">
                                {rule.annotations.summary}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={severity}
                              size="small"
                              className={classes.severityChip}
                              style={{
                                backgroundColor: severityColor(severity),
                                color: '#fff',
                              }}
                            />
                          </TableCell>
                          <TableCell align="right" className={classes.thresholdCell}>
                            {rule._threshold != null
                              ? `${operatorLabel(rule._operator ?? 'gt')} ${rule._threshold}`
                              : '-'}
                          </TableCell>
                          <TableCell className={classes.forCell}>
                            {formatFor(rule.for)}
                          </TableCell>
                          <TableCell>
                            <Chip label={component} size="small" variant="outlined" style={{ height: 20, fontSize: '0.7rem' }} />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip
                              title={
                                rule.provenance === 'file'
                                  ? 'Git으로 관리됨 (grafana-config.yaml) — 코드에서 수정'
                                  : '임계치 편집'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => openEdit(rule)}
                                  disabled={rule.provenance === 'file'}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Card>
        ))}

            {!loading && groups.length === 0 && !error && (
              <Typography color="textSecondary" align="center">
                등록된 알람 룰이 없습니다.
              </Typography>
            )}
          </>
        )}

        {/* ═══ Tab 1: 실시간 현황 ═══ */}
        {activeTab === 1 && (
          <>
            <ContentHeader title="">
              <Tooltip title="새로고침">
                <IconButton onClick={fetchAlerts} disabled={acLoading}>
                  {acLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
              <SupportButton>
                Grafana Unified Alerting 기반 실시간 알람 현황입니다.
                알람 발생/대기/정상 상태를 모니터링하고, 필터로 원하는 알람만 확인할 수 있습니다.
              </SupportButton>
            </ContentHeader>

            {/* Stats */}
            <Box className={classes.statsRow}>
              <Card variant="outlined" className={classes.statCard}>
                <CardContent>
                  <Typography className={classes.statValue} style={{ color: '#f44336' }}>{alertingCount}</Typography>
                  <Typography className={classes.statLabel}>발생 중</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" className={classes.statCard}>
                <CardContent>
                  <Typography className={classes.statValue} style={{ color: '#ff9800' }}>{pendingCount}</Typography>
                  <Typography className={classes.statLabel}>대기</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" className={classes.statCard}>
                <CardContent>
                  <Typography className={classes.statValue} style={{ color: '#4caf50' }}>{normalCount}</Typography>
                  <Typography className={classes.statLabel}>정상</Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Filters */}
            <Box className={classes.filterRow}>
              <FormControl variant="outlined" size="small" className={classes.formControl}>
                <InputLabel>상태</InputLabel>
                <Select
                  value={stateFilter}
                  onChange={e => setStateFilter(e.target.value as string)}
                  label="상태"
                >
                  <MenuItem value="all">전체</MenuItem>
                  <MenuItem value="Alerting">발생 중</MenuItem>
                  <MenuItem value="Pending">대기</MenuItem>
                  <MenuItem value="Normal">정상</MenuItem>
                  <MenuItem value="NoData">데이터 없음</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" className={classes.formControl}>
                <InputLabel>심각도</InputLabel>
                <Select
                  value={severityFilter}
                  onChange={e => setSeverityFilter(e.target.value as string)}
                  label="심각도"
                >
                  <MenuItem value="all">전체</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {acError && (
              <Box mb={2}>
                <Typography color="error">{acError}</Typography>
              </Box>
            )}

            {/* Alert Table */}
            <Card variant="outlined">
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>상태</TableCell>
                      <TableCell>룰</TableCell>
                      <TableCell>그룹</TableCell>
                      <TableCell>심각도</TableCell>
                      <TableCell>컴포넌트</TableCell>
                      <TableCell>요약</TableCell>
                      <TableCell>발생 시각</TableCell>
                      <TableCell>마지막 평가</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAlerts.map((alert, idx) => (
                      <TableRow key={`${alert.ruleName}-${idx}`} className={classes.timelineRow}>
                        <TableCell>
                          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                            {acStateIcon(alert.state)}
                            <Chip
                              label={acStateLabel(alert.state)}
                              size="small"
                              className={classes.stateChip}
                              style={{
                                backgroundColor: acStateColor(alert.state),
                                color: '#fff',
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {alert.ruleName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="textSecondary">
                            {alert.group}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={alert.severity}
                            size="small"
                            className={classes.stateChip}
                            style={{
                              backgroundColor: alert.severity === 'critical' ? '#f44336' : alert.severity === 'warning' ? '#ff9800' : '#9e9e9e',
                              color: '#fff',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip label={alert.component} size="small" variant="outlined" style={{ height: 20, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {alert.summary || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell className={classes.mono}>
                          {acFormatTime(alert.activeAt)}
                        </TableCell>
                        <TableCell className={classes.mono}>
                          {acFormatTime(alert.lastEvaluation)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredAlerts.length === 0 && !acLoading && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Box py={3} display="flex" flexDirection="column" alignItems="center" style={{ gap: 8 }}>
                            <NotificationsIcon style={{ fontSize: 40, color: '#bdbdbd' }} />
                            <Typography color="textSecondary">
                              {stateFilter !== 'all' || severityFilter !== 'all'
                                ? '필터 조건에 맞는 알람이 없습니다.'
                                : '등록된 알람이 없습니다.'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </>
        )}

        {/* Edit Dialog (shared) */}
        <Dialog open={!!editRule} onClose={() => setEditRule(null)} maxWidth="sm" fullWidth>
          <DialogTitle>알람 룰 임계치 편집</DialogTitle>
          <DialogContent>
            {editRule && (
              <Box display="flex" flexDirection="column" style={{ gap: 16, paddingTop: 8 }}>
                <Typography variant="subtitle2">{editRule.title}</Typography>
                {editRule.annotations?.description && (
                  <Typography variant="body2" color="textSecondary">
                    {editRule.annotations.description}
                  </Typography>
                )}
                <TextField
                  label="임계치"
                  type="number"
                  value={editThreshold}
                  onChange={e => setEditThreshold(e.target.value)}
                  variant="outlined"
                  size="small"
                  helperText={`현재: ${editRule._threshold ?? 'N/A'} (${operatorLabel(editRule._operator ?? 'gt')})`}
                />
                <TextField
                  label="대기 시간 (for)"
                  value={editFor}
                  onChange={e => setEditFor(e.target.value)}
                  variant="outlined"
                  size="small"
                  helperText="예: 3m, 5m, 10m"
                />
                {saveError && (
                  <Typography color="error" variant="body2">{saveError}</Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditRule(null)} disabled={saving}>
              취소
            </Button>
            <Button
              onClick={handleSave}
              color="primary"
              variant="contained"
              disabled={saving}
            >
              {saving ? <CircularProgress size={20} /> : '저장'}
            </Button>
          </DialogActions>
        </Dialog>
      </Content>
    </Page>
  );
};
